import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent } from '../ui/Modal';
import { Button, Input, Select } from '../ui';
import { formatPhoneKEForInput, normalizePhoneKE } from '../../utils/formatters';
import { Mail, Phone, User, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentUserRole: string; // LANDLORD (OWNER) or ADMIN — from org membership when available
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  overflow: visible;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: visible;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Required = styled.span`
  color: ${props => props.theme.destructive || '#ef4444'};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.destructive || '#ef4444'};
  font-size: 14px;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.success || '#10b981'};
  font-size: 14px;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const RoleDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
  margin-top: 4px;
  padding: 8px;
  background: ${props => props.theme.muted}30;
  border-radius: 6px;
`;

const getRoleDescription = (role: string): string => {
  switch (role) {
    case 'ADMIN':
      return 'Full access to all features. Can manage properties, tenants, payments, agents, and caretakers. Can be invited by landlords.';
    case 'AGENT':
      return 'Limited access to assigned properties. Can manage tenants and payments for assigned properties.';
    case 'CARETAKER':
      return 'Read-only access to assigned properties. Can view maintenance requests and property information.';
    default:
      return '';
  }
};

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentUserRole
}) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Determine available roles based on PMS role (OWNER maps to LANDLORD)
  const availableRoles = currentUserRole === 'LANDLORD' || currentUserRole === 'OWNER'
    ? [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'AGENT', label: 'Agent' },
        { value: 'CARETAKER', label: 'Caretaker' }
      ]
    : [
        { value: 'AGENT', label: 'Agent' },
        { value: 'CARETAKER', label: 'Caretaker' }
      ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    setError(null);
    setSuccess(null);

    if (!email || !phone || !role) {
      setError('Email, phone, and role are required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const { data } = await apiService.inviteUser({
        email: email.toLowerCase().trim(),
        role,
        phone: phone.trim()
      });

      if (data.emailSent) {
        setSuccess(`Invitation sent successfully to ${email}. The user will receive an email with instructions to set up their account.`);
      } else {
        const invitationUrl = data.invitation?.invitationUrl;
        if (invitationUrl) {
          setSuccess(`User invited successfully, but the email could not be sent. Please share this invitation link manually: ${invitationUrl}`);
        } else {
          setSuccess(`User invited successfully, but the email could not be sent. Please contact the user directly.`);
        }
      }
      
      // Reset form
      setEmail('');
      setPhone('');
      setRole('');
      
      // Call onSuccess callback after a short delay to refresh users list
      setTimeout(() => {
        onSuccess(); // This will refresh the users list
        onClose();
        setSuccess(null);
      }, 2000);
    } catch (err: any) {
      console.error('Error inviting user:', err);
      const message = err?.response?.data?.error || err?.message || 'Failed to invite user. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setPhone('');
      setRole('');
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite User" size="sm" zIndex={2000}>
      <ModalContent>
        <FormContainer>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>
                <Mail size={16} />
                Email Address
                <Required>*</Required>
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={loading}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Phone size={16} />
                Phone Number <Required>*</Required>
              </Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneKEForInput(e.target.value))}
                placeholder="254700000000"
                disabled={loading}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <User size={16} />
                Role
                <Required>*</Required>
              </Label>
              <Select
                value={role}
                onChange={(value) => setRole(value as string)}
                options={availableRoles}
                placeholder="Select a role"
                disabled={loading}
                required
                fullWidth={true}
              />
              {role && (
                <RoleDescription>
                  {getRoleDescription(role)}
                </RoleDescription>
              )}
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <ButtonGroup>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !email || !role}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Sending Invitation...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </ButtonGroup>
          </form>
        </FormContainer>
      </ModalContent>
    </Modal>
  );
};

